import { useMemo } from 'react'

import type { Vitals } from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'

interface WeightPoint {
  weight: number
  recordedAt: string
}

// Chart canvas dimensions and padding
const W = 480
const H = 200
const PAD = { top: 16, right: 16, bottom: 32, left: 44 }
// Extra horizontal inset so the first/last nodes don't sit right on the axes
const DATA_INSET_X = 16

const MS_PER_MONTH = 1000 * 60 * 60 * 24 * 30.44

/**
 * Picks the smallest tick interval (in months) such that the number of tick
 * intervals across the given span stays at or below 4 (i.e. at most 5 tick points).
 */
function pickTickIntervalMonths(spanMonths: number): number {
  for (const iv of [1, 2, 3, 6, 12, 24, 36, 60]) {
    if (spanMonths / iv <= 4) return iv
  }
  return 60
}

/**
 * Generates calendar-aligned X-axis ticks for a time range.
 * Ticks fall on the 1st of each month at the chosen interval, formatted as
 * "Jun '24" for sub-year intervals or "2024" for yearly+.
 */
function generateXTicks(minTime: number, maxTime: number): Array<{ time: number; label: string }> {
  if (minTime === maxTime) return []

  const spanMonths = (maxTime - minTime) / MS_PER_MONTH
  if (spanMonths < 1) return []

  const intervalMonths = pickTickIntervalMonths(spanMonths)

  const minDate = new Date(minTime)
  const alignedMonth = Math.ceil(minDate.getMonth() / intervalMonths) * intervalMonths
  let year = minDate.getFullYear() + Math.floor(alignedMonth / 12)
  let month = alignedMonth % 12

  const ticks: Array<{ time: number; label: string }> = []

  while (ticks.length <= 4) {
    const d = new Date(year, month, 1)
    const t = d.getTime()
    if (t > maxTime) break

    const label =
      intervalMonths >= 12
        ? String(year)
        : d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })

    ticks.push({ time: t, label })

    month += intervalMonths
    year += Math.floor(month / 12)
    month = month % 12
  }

  return ticks
}

/** Computes all SVG geometry needed to render the weight line chart. */
function useChartGeometry(points: WeightPoint[]) {
  return useMemo(() => {
    if (points.length === 0) return null

    const times = points.map((p) => new Date(p.recordedAt).getTime())
    const weights = points.map((p) => p.weight)

    const minTime = Math.min(...times)
    const maxTime = Math.max(...times)
    const minW = Math.min(...weights)
    const maxW = Math.max(...weights)

    // Add 10% vertical padding so the line doesn't hug the edges
    const wPadding = (maxW - minW) * 0.1 || 1
    const lo = Math.max(0, minW - wPadding)
    const hi = maxW + wPadding

    const innerW = W - PAD.left - PAD.right
    const innerH = H - PAD.top - PAD.bottom

    // Expand to a minimum 1-month display range so short-span data still gets X axis ticks
    const anchor = new Date(minTime)
    const displayMinTime =
      maxTime - minTime < MS_PER_MONTH
        ? new Date(anchor.getFullYear(), anchor.getMonth(), 1).getTime()
        : minTime
    const displayMaxTime =
      maxTime - minTime < MS_PER_MONTH
        ? new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1).getTime()
        : maxTime

    const timeRange = Math.max(displayMaxTime - displayMinTime, 1)
    const toX = (t: number) =>
      PAD.left + DATA_INSET_X + ((t - displayMinTime) / timeRange) * (innerW - 2 * DATA_INSET_X)
    const toY = (w: number) => PAD.top + (1 - (w - lo) / (hi - lo)) * innerH

    const coords = points.map((p) => ({
      x: toX(new Date(p.recordedAt).getTime()),
      y: toY(p.weight),
      point: p,
    }))

    const polylinePoints = coords.map((c) => `${c.x},${c.y}`).join(' ')

    // 4 evenly spaced Y ticks
    const yTicks = Array.from({ length: 4 }, (_, i) => {
      const w = lo + (i / 3) * (hi - lo)
      return { y: toY(w), label: w.toFixed(1) }
    })

    const xTicks = generateXTicks(displayMinTime, displayMaxTime).map((tick) => ({
      x: toX(tick.time),
      label: tick.label,
    }))

    return { coords, polylinePoints, yTicks, xTicks }
  }, [points])
}

function WeightLineChart({ points }: { points: WeightPoint[] }) {
  const geom = useChartGeometry(points)

  if (!geom) {
    return (
      <div
        style={{ height: H }}
        className="flex w-full items-center justify-center text-muted-foreground"
      >
        No weight data
      </div>
    )
  }

  return (
    <svg
      width="100%"
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      overflow="visible"
      role="img"
      aria-label="Weight history chart"
    >
      {/* Horizontal grid lines */}
      {geom.yTicks.map((tick) => (
        <line
          key={tick.label}
          x1={PAD.left}
          y1={tick.y}
          x2={W - PAD.right}
          y2={tick.y}
          className="stroke-border"
          strokeWidth={1}
        />
      ))}

      {/* Y axis labels */}
      {geom.yTicks.map((tick) => (
        <text
          key={tick.label}
          x={PAD.left - 6}
          y={tick.y}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={10}
          className="fill-muted-foreground"
        >
          {tick.label}
        </text>
      ))}

      {/* X axis labels */}
      {geom.xTicks.map((tick) => (
        <text
          key={tick.label}
          x={tick.x}
          y={H - 6}
          textAnchor="middle"
          fontSize={10}
          className="fill-muted-foreground"
        >
          {tick.label}
        </text>
      ))}

      {/* Drop lines from each node to the X axis */}
      {geom.coords.map((c, i) => (
        <line
          // biome-ignore lint/suspicious/noArrayIndexKey: stable ordered list
          key={i}
          x1={c.x}
          y1={c.y + 4}
          x2={c.x}
          y2={H - PAD.bottom}
          className="stroke-primary"
          strokeWidth={1}
          strokeOpacity={0.25}
        />
      ))}

      {/* Weight line */}
      {geom.coords.length > 1 && (
        <polyline
          points={geom.polylinePoints}
          fill="none"
          className="stroke-primary"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Data point dots */}
      {geom.coords.map((c, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: stable ordered list
        <circle key={i} cx={c.x} cy={c.y} r={4} className="fill-primary cursor-pointer">
          <title>
            {/* Vitals carry no clinic — same as the table below. */}
            {c.point.weight.toFixed(2)} kg ·{' '}
            <FormattedDate value={c.point.recordedAt} clinicId={null} />
          </title>
        </circle>
      ))}
    </svg>
  )
}

/**
 * Weight history dialog. Opened externally; renders the chart and table
 * for a patient's weight readings over time.
 */
export function WeightChartDialog({
  vitals,
  open,
  onOpenChange,
}: {
  vitals: Vitals[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const weightPoints = useMemo(
    () =>
      vitals
        .filter((v) => v.weight != null)
        .map((v) => ({ weight: Number.parseFloat(v.weight!), recordedAt: v.createdAt }))
        .reverse(),
    [vitals]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Weight History</DialogTitle>
        </DialogHeader>
        <div className="px-4">
          <WeightLineChart points={weightPoints} />
        </div>
        <div className="max-h-[240px] overflow-y-auto px-6">
          <Table size="sm" variant="borderless">
            <TableHeader className="sticky top-0 bg-popover">
              <TableRow>
                <TableHead className="text-center">Date</TableHead>
                <TableHead className="text-center">Weight (kg)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...weightPoints].reverse().map((p, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: stable ordered list
                <TableRow key={i}>
                  <TableCell className="text-center text-muted-foreground">
                    <FormattedDate value={p.recordedAt} clinicId={null} />
                  </TableCell>
                  <TableCell className="text-center">{p.weight.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}
